import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getWidgetDataURI, apiGet, toggleWidgetTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { InsiderHistoricalActivityConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface InsiderHistoricalTransaction {
    symbol: string;
    filing_date: string;
    transaction_date: string;
    transaction_amount: number;
    transaction_type: string;
    securities_transacted: number;
    price: number;
    reporting_name: string;
    acquisition_or_disposition: string;
}

interface InsiderHistoricalActivityApiData {
    parameters: {
        insider_name: string;
        days_back: number;
        min_transaction_value: number;
        cutoff_date: string;
        query_date: string;
    };
    matched_insiders: string[];
    summary: {
        total_transactions: number;
        total_value: number;
        avg_transaction_value: number;
        min_transaction: number;
        max_transaction: number;
        earliest_filing: string | null;
        latest_filing: string | null;
        unique_symbols: number;
        acquisition_count: number;
        disposition_count: number;
        total_acquisition_value: number;
        total_disposition_value: number;
    };
    acquisitions: { count: number; data: InsiderHistoricalTransaction[] };
    dispositions: { count: number; data: InsiderHistoricalTransaction[] };
    all_transactions: InsiderHistoricalTransaction[];
    chart_data: {
        acquisitions: { dates: string[]; values: number[]; symbols: string[] };
        dispositions: { dates: string[]; values: number[]; symbols: string[] };
    };
}

type DateSelectionKey = 'today' | '1w_ago' | '1m_ago' | '1y_ago';

interface ChartSnapshot {
    date: string;
    data: Array<{ symbol: string; value: number }>;
}

const DATE_SELECTION_OFFSETS: Record<DateSelectionKey, number> = {
    today: 0,
    '1w_ago': 7,
    '1m_ago': 30,
    '1y_ago': 365
};

const FRIENDLY_DATE_LABELS: Record<DateSelectionKey, string> = {
    today: 'Today',
    '1w_ago': '1W Ago',
    '1m_ago': '1M Ago',
    '1y_ago': '1Y Ago'
};

export class TraderverseInsiderHistoricalActivity {
    private container: HTMLElement;
    private config: InsiderHistoricalActivityConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private apiData: InsiderHistoricalActivityApiData | null = null;
    private chartSnapshots: ChartSnapshot[] = [];
    private currentChart: any;
    private selectedDateSelections: DateSelectionKey[] = ['today', '1w_ago', '1m_ago', '1y_ago'];
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        insiderName: '' as string,
        daysBack: 90 as number | '',
        minTransactionValue: 500000 as number | ''
    };

    constructor(config: InsiderHistoricalActivityConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<InsiderHistoricalActivityConfig> {
        return {
            headerPinned: true,
            insiderName: '',
            daysBack: 90,
            minTransactionValue: 500000,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private applyConfigFilters(): void {
        if (typeof this.config.insiderName === 'string') this.filters.insiderName = this.config.insiderName;
        if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
        if (typeof this.config.minTransactionValue === 'number') this.filters.minTransactionValue = this.config.minTransactionValue;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        if (this.filters.insiderName.trim()) {
            await this.fetchData();
        } else {
            this.updateDisplay();
        }
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Insider Historical Activity</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
                <div class="item">
                  <input type="text" class="filter-insider-name" placeholder="Search insider name..." value="${this.escapeHtml(this.filters.insiderName)}" aria-label="Insider name" />
                </div>
                <span></span>
                <div class="item">
                    <div class="legendsCollection">
                        <span class="toggle-date" data-value="today" data-tooltip="Today" style="background-color:#7C4DFF;"></span>
                        <span class="toggle-date" data-value="1w_ago" data-tooltip="1 Week Ago" style="background-color:#33D17A;"></span>
                        <span class="toggle-date" data-value="1m_ago" data-tooltip="1 Month Ago" style="background-color:#F3C13A;"></span>
                        <span class="toggle-date" data-value="1y_ago" data-tooltip="1 Year Ago" style="background-color:#FF9A4D;"></span>
                    </div>
                </div>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Days Back</label>
                    <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
                  </div>
                  <div class="item">
                    <label>Min Transaction Value</label>
                    <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
                  </div>
                </div>
            </div>
            <div class="chartDv">
                <div class="chart" id="marketRealtimeChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        this.setupLegendToggles();
        setupHeaderPin(this.shadowRoot, this.config);
        this.setupEventListeners();
    }

    private setupLegendToggles(): void {
        const legendContainer = this.shadowRoot.querySelector('.legendsCollection') as HTMLElement | null;
        if (!legendContainer) return;

        const toggles = Array.from(legendContainer.querySelectorAll('.toggle-date')) as HTMLElement[];
        toggles.forEach(t => {
            const val = (t.getAttribute('data-value') || '') as DateSelectionKey;
            const isActive = this.selectedDateSelections.indexOf(val) !== -1;
            t.setAttribute('role', 'button');
            t.setAttribute('aria-pressed', isActive.toString());
            t.setAttribute('aria-selected', isActive.toString());
            t.tabIndex = 0;
            if (isActive) t.classList.add('active');
            else t.classList.remove('active');
            t.addEventListener('keydown', (ev) => {
                if ((ev as KeyboardEvent).key === ' ' || (ev as KeyboardEvent).key === 'Enter') {
                    ev.preventDefault();
                    (ev.currentTarget as HTMLElement).click();
                }
            });
        });

        legendContainer.addEventListener('click', (ev) => {
            const target = (ev.target as HTMLElement).closest('.toggle-date') as HTMLElement | null;
            if (!target) return;
            const val = (target.getAttribute('data-value') || '') as DateSelectionKey;
            const idx = this.selectedDateSelections.indexOf(val);
            if (idx === -1) {
                this.selectedDateSelections.push(val);
            } else if (this.selectedDateSelections.length > 1) {
                this.selectedDateSelections.splice(idx, 1);
            } else {
                return;
            }

            toggles.forEach(t => {
                const v = (t.getAttribute('data-value') || '') as DateSelectionKey;
                const active = this.selectedDateSelections.indexOf(v) !== -1;
                t.classList.toggle('active', active);
                t.setAttribute('aria-pressed', active.toString());
                t.setAttribute('aria-selected', active.toString());
            });

            this.buildChartSnapshots();
            this.updateDisplay();
        });
    }

    private setupEventListeners(): void {
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        const insiderInput = this.shadowRoot.querySelector('.filter-insider-name') as HTMLInputElement | null;
        const runSearch = () => {
            this.filters.insiderName = insiderInput?.value.trim() ?? '';
            this.config.insiderName = this.filters.insiderName;
            this.fetchData();
        };
        insiderInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') runSearch();
        });
        insiderInput?.addEventListener('change', runSearch);

        const updateNumericFiltersAndFetch = () => {
            this.filters.daysBack = this.readNumberInput('.filter-days-back');
            this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
            if (this.filters.daysBack !== '') this.config.daysBack = this.filters.daysBack;
            if (this.filters.minTransactionValue !== '') this.config.minTransactionValue = this.filters.minTransactionValue;
            if (this.filters.insiderName.trim()) this.fetchData();
        };

        ['.filter-days-back', '.filter-min-transaction-value'].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {
            insiderName: this.filters.insiderName.trim()
        };
        if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
        if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
        return query;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async fetchData(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const chartEl = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
        if (widget) widget.classList.add('loading');

        if (!this.filters.insiderName.trim()) {
            this.apiData = null;
            this.chartSnapshots = [];
            this.updateDisplay();
            if (widget) widget.classList.remove('loading');
            return;
        }

        try {
            const response = await apiGet({
                url: `${APIURL}/v1/equity-market/insider-historical-activity`,
                query: this.buildQueryParams()
            });

            if (!response.ok) throw new Error('Failed to fetch insider historical activity data');
            const json = await response.json();
            this.apiData = (json?.data as InsiderHistoricalActivityApiData | undefined) ?? null;
            this.buildChartSnapshots();

            if (this.chartSnapshots.length > 0) {
                chartEl?.setAttribute('data-testing-id', 'insider-historical-activity');
            } else {
                chartEl?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Error fetching insider historical activity data:', err);
            this.apiData = null;
            this.chartSnapshots = [];
            this.updateDisplay();
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private parseDate(value: string): Date | null {
        if (!value) return null;
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    private formatDate(date: Date): string {
        return date.toISOString().slice(0, 10);
    }

    private addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() - days);
        return result;
    }

    private buildChartSnapshots(): void {
        if (!this.apiData?.all_transactions?.length) {
            this.chartSnapshots = [];
            return;
        }

        const queryDate = this.parseDate(this.apiData.parameters?.query_date ?? '');
        if (!queryDate) {
            this.chartSnapshots = [];
            return;
        }

        const transactions = this.apiData.all_transactions;
        const selections = this.selectedDateSelections.length
            ? this.selectedDateSelections
            : (['today'] as DateSelectionKey[]);

        this.chartSnapshots = selections.map((selection) => {
            const cutoff = this.addDays(queryDate, DATE_SELECTION_OFFSETS[selection]);
            const cutoffStr = this.formatDate(cutoff);
            const totals = new Map<string, number>();

            transactions.forEach((tx) => {
                const txDate = this.parseDate(tx.transaction_date || tx.filing_date);
                if (!txDate || txDate > cutoff) return;
                const amount = typeof tx.transaction_amount === 'number'
                    ? tx.transaction_amount
                    : Number(tx.transaction_amount);
                if (Number.isNaN(amount)) return;
                totals.set(tx.symbol, (totals.get(tx.symbol) ?? 0) + amount);
            });

            const data = Array.from(totals.entries())
                .map(([symbol, value]) => ({ symbol, value }))
                .sort((a, b) => b.value - a.value);

            return { date: cutoffStr, data };
        });
    }

    private updateDisplay(): void {
        const chartEl = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
        if (!this.filters.insiderName.trim()) {
            if (chartEl) {
                chartEl.removeAttribute('data-testing-id');
                chartEl.innerHTML = '<p style="text-align:center;padding:40px 16px;">Enter an insider name to view historical activity</p>';
            }
            if (this.currentChart) {
                this.currentChart.dispose();
                this.currentChart = null;
            }
            return;
        }

        if (!this.apiData?.all_transactions?.length) {
            if (chartEl) {
                chartEl.removeAttribute('data-testing-id');
                chartEl.innerHTML = getNoDataHTML();
            }
            if (this.currentChart) {
                this.currentChart.dispose();
                this.currentChart = null;
            }
            return;
        }

        if (chartEl) chartEl.innerHTML = '';

        if (!this.chartSnapshots.length || !(window as any).echarts) {
            if (!(window as any).echarts) {
                setTimeout(() => this.updateDisplay(), 100);
            }
            return;
        }

        this.drawChart();
    }

    private formatCurrency(value: number): string {
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
        return `$${value.toFixed(0)}`;
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
        if (!container || !this.chartSnapshots.length) return;

        const symbolSet = new Set<string>();
        this.chartSnapshots.forEach((snapshot) => {
            snapshot.data.forEach((point) => symbolSet.add(point.symbol));
        });

        const symbols = Array.from(symbolSet);
        symbols.sort((a, b) => {
            const maxA = Math.max(...this.chartSnapshots.map(s => s.data.find(d => d.symbol === a)?.value ?? 0));
            const maxB = Math.max(...this.chartSnapshots.map(s => s.data.find(d => d.symbol === b)?.value ?? 0));
            return maxB - maxA;
        });

        const topSymbols = symbols.slice(0, 12);
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const colorList = ['#7C4DFF', '#33D17A', '#F3C13A', '#FF9A4D'];

        const seriesData: any[] = [];
        const selections = this.selectedDateSelections.length
            ? this.selectedDateSelections
            : (['today'] as DateSelectionKey[]);

        selections.forEach((selection, idx) => {
            const snapshot = this.chartSnapshots[idx];
            if (!snapshot) return;

            const valueMap = new Map(snapshot.data.map((point) => [point.symbol, point.value]));
            const values = topSymbols.map((symbol) => valueMap.get(symbol) ?? null);
            const seriesName = FRIENDLY_DATE_LABELS[selection] || snapshot.date;

            seriesData.push({
                name: seriesName,
                type: 'line',
                smooth: true,
                data: values,
                lineStyle: { color: colorList[idx % colorList.length], width: 2.5 },
                itemStyle: { color: colorList[idx % colorList.length] },
                symbol: 'circle',
                symbolSize: 6,
                showSymbol: true,
                emphasis: {
                    focus: 'series',
                    itemStyle: { borderColor: axisLineColor, borderWidth: 1 }
                }
            });
        });

        const toggles = Array.from(this.shadowRoot.querySelectorAll('.toggle-date')) as HTMLElement[];
        toggles.forEach(t => {
            const v = t.getAttribute('data-value') || '';
            const active = this.selectedDateSelections.indexOf(v as DateSelectionKey) !== -1;
            t.classList.toggle('active', active);
        });

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    if (!params?.length) return '';
                    let html = `<div style="padding:4px 0;font-weight:600;">${params[0].axisValue}</div>`;
                    params.forEach((p: any) => {
                        const v = p.data;
                        const display = v == null ? '-' : this.formatCurrency(v);
                        html += `<div style="display:flex;align-items:center;margin:4px 0"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:8px"></span><span>${p.seriesName}: ${display}</span></div>`;
                    });
                    return html;
                }
            },
            grid: { top: 10, left: 0, right: 0, bottom: 0, containLabel: true },
            xAxis: {
                type: 'category',
                data: topSymbols,
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: { color: textColor, fontSize: 12 },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                axisLine: { lineStyle: { color: axisLineColor } },
                splitLine: { lineStyle: { color: splitLineColor } },
                axisLabel: {
                    color: textColor,
                    fontSize: 12,
                    formatter: (v: number) => this.formatCurrency(v)
                }
            },
            series: seriesData
        };

        const chart = echarts.init(container);
        chart.setOption(option);
        this.currentChart = chart;
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseInsiderHistoricalActivity: typeof TraderverseInsiderHistoricalActivity; } }
if (typeof window !== 'undefined') { window.TraderverseInsiderHistoricalActivity = TraderverseInsiderHistoricalActivity; }
